/**
 * MFAVerifyModal — Modal de vérification MFA lors du login.
 *
 * Affiché après un login email/password réussi, si l'utilisateur
 * a un facteur TOTP vérifié. Bloque l'accès à l'app tant que le
 * code n'est pas validé (aal2 non atteint).
 *
 * Props :
 *   open      {boolean}  — contrôle l'affichage
 *   factorId  {string}   — ID du facteur TOTP à vérifier
 *   onSuccess {Function} — appelé quand le code est correct
 *   onCancel  {Function} — appelé si l'utilisateur annule (déclenche logout)
 */
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ShieldCheck, Loader2 } from 'lucide-react';

export default function MFAVerifyModal({ open, factorId, onSuccess, onCancel }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Réinitialiser à chaque ouverture
  useEffect(() => {
    if (open) {
      setCode('');
      setError('');
    }
  }, [open]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Le code doit contenir 6 chiffres');
      return;
    }
    setLoading(true);
    setError('');

    try {
      // 1) Créer un challenge pour ce facteur
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeErr) throw challengeErr;

      // 2) Vérifier le code TOTP
      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyErr) throw verifyErr;

      // 3) aal2 atteint → accès autorisé
      onSuccess();
    } catch (err) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('expired')) {
        setError('Code expiré, réessayez.');
      } else {
        setError('Code invalide. Vérifiez votre application d\'authentification.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent className="sm:max-w-sm" onPointerDownOutside={(e) => e.preventDefault()}>
        {/* Titre visible uniquement pour les lecteurs d'écran (exigé par Radix UI) */}
        <DialogTitle className="sr-only">Vérification MFA</DialogTitle>
        <DialogDescription className="sr-only">Entrez le code de votre application d'authentification</DialogDescription>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            Authentification à 2 facteurs
          </DialogTitle>
          <DialogDescription>
            Entrez le code à 6 chiffres généré par votre application d'authentification.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleVerify} className="space-y-4 pt-2">
          <Input
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              setError('');
            }}
            placeholder="000000"
            className="text-center font-mono text-2xl tracking-[0.4em]"
            maxLength={6}
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
            disabled={loading}
          />

          {error && (
            <p className="text-sm text-red-500 text-center">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full"
          >
            {loading
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Vérification...</>
              : 'Valider'
            }
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={loading}
            className="w-full text-gray-500"
          >
            Annuler la connexion
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
